import type { AccessCatalogItemPublic } from '@accounts/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../../../i18n';
import { updateAccessCatalogItem } from '../../../../lib/api';
import { formatDate } from '../../../../lib/format';
import { showToast } from '../../../../lib/toast';
import { EmptyRow } from '../../../../ui/Primitives';
import { checkboxStyle, rowActionStyle, searchFieldStyle } from '../../../../ui/styles';
import { pathLabel } from '../accessFormat';

import { groupRows, rowShort } from './hierarchy';
import { errorText, useInvalidateAccess } from './queries';

type Toggle = { item: AccessCatalogItemPublic; field: 'requestable' | 'hrCanGrant'; value: boolean };

/**
 * The "Catalog rows" section: one table grouped by top-level entry, with Requestable and
 * HR can grant as switches that save at once (the demo's rule: "switches save at once").
 * Everything else about a row is edited in its drawer, which the row's name opens.
 *
 * `holders` is computed by the caller from the reads it already holds — records for an
 * outside row, live grants for an app row — so this table issues no reads of its own.
 */
export function CatalogRowsTable({
  items,
  holders,
  onOpen,
  onAdd,
}: {
  items: readonly AccessCatalogItemPublic[];
  holders: (item: AccessCatalogItemPublic) => number | null;
  onOpen: (id: string) => void;
  /** Absent for a view-only reader (SIR-7864): no Add row, and the switches are inert. */
  onAdd?: (() => void) | undefined;
}) {
  const readOnly = onAdd === undefined;
  const invalidate = useInvalidateAccess();
  const [search, setSearch] = useState('');
  const [showRetired, setShowRetired] = useState(false);

  const toggle = useMutation({
    mutationFn: ({ item, field, value }: Toggle) =>
      updateAccessCatalogItem(
        item.id,
        field === 'requestable' ? { requestable: value } : { hrCanGrant: value },
      ),
    onSuccess: async (_response, { item, field, value }) => {
      await invalidate();
      if (field === 'requestable') {
        showToast(
          value
            ? STR.access.catalog.savedRequestableOn(item.name)
            : STR.access.catalog.savedRequestableOff(item.name),
        );
      } else {
        showToast(value ? STR.access.catalog.savedHrOn(item.name) : STR.access.catalog.savedHrOff(item.name));
      }
    },
    onError: (failure) => {
      showToast(errorText(failure), 'warn');
    },
  });

  const needle = search.trim().toLowerCase();
  const shown = items.filter(
    (item) =>
      (showRetired || item.retiredAt === null) &&
      (needle === '' ||
        `${item.name} ${pathLabel(item.path)} ${item.application?.name ?? ''}`
          .toLowerCase()
          .includes(needle)),
  );
  const groups = groupRows(shown);
  const pendingId = toggle.isPending ? (toggle.variables?.item.id ?? null) : null;

  const sw = (item: AccessCatalogItemPublic, field: Toggle['field'], label: string) => (
    <input
      type="checkbox"
      role="switch"
      aria-label={label}
      checked={item[field]}
      disabled={readOnly || item.retiredAt !== null || pendingId === item.id}
      onChange={(event) => {
        toggle.mutate({ item, field, value: event.target.checked });
      }}
      style={checkboxStyle}
    />
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          padding: '10px 18px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <input
          className="field"
          type="search"
          value={search}
          placeholder={STR.access.catalog.searchPlaceholder}
          aria-label={STR.access.catalog.searchLabel}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          style={{ ...searchFieldStyle, width: 240 }}
        />
        <label
          className="tx-subtle"
          style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5 }}
        >
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(event) => {
              setShowRetired(event.target.checked);
            }}
            style={{ ...checkboxStyle, margin: 0 }}
          />
          {STR.access.catalog.showRetired}
        </label>
        <span
          className="tx-subtle"
          style={{ marginLeft: 'auto', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
        >
          {STR.access.catalog.rowCount(shown.length)}
        </span>
        {readOnly ? null : (
          <button type="button" className="ghost" style={rowActionStyle} onClick={onAdd}>
            {STR.access.catalog.addRow}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyRow>{STR.access.catalog.noRows}</EmptyRow>
      ) : (
        <div className="dscroll">
          <table className="dtable" style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th>{STR.access.catalog.colRow}</th>
                <th>{STR.access.catalog.colPlace}</th>
                <th>{STR.access.catalog.colApp}</th>
                <th>{STR.access.catalog.colRequestable}</th>
                <th>{STR.access.catalog.colHrCanGrant}</th>
                <th className="right">{STR.access.catalog.colPeople}</th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyRow>{STR.access.catalog.noRowMatch}</EmptyRow>
                  </td>
                </tr>
              ) : null}
              {groups.map(([group, rows]) => [
                <tr key={`group-${group}`}>
                  <td
                    colSpan={6}
                    className="surface-2 tx-subtle"
                    style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', padding: '7px 14px' }}
                  >
                    {group}{' '}
                    <span style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                      {rows.length}
                    </span>
                  </td>
                </tr>,
                ...rows.map((item) => {
                  const count = holders(item);
                  return (
                    <tr key={item.id} {...(item.retiredAt === null ? {} : { 'data-off': '' })}>
                      <td>
                        <button
                          type="button"
                          className="tbtn"
                          onClick={() => {
                            onOpen(item.id);
                          }}
                          style={{
                            padding: 0,
                            border: 'none',
                            fontWeight: 650,
                            fontSize: 12.5,
                            color: 'var(--text)',
                            textAlign: 'left',
                            cursor: 'pointer',
                            whiteSpace: 'normal',
                          }}
                        >
                          {rowShort(item)}
                        </button>
                        {item.retiredAt === null ? null : (
                          <span className="tx-subtle" style={{ fontSize: 11, marginLeft: 8 }}>
                            {STR.access.catalog.retiredOn(formatDate(item.retiredAt))}
                          </span>
                        )}
                      </td>
                      <td className="tx-subtle" style={{ fontSize: 12.5 }}>
                        {pathLabel(item.path)}
                      </td>
                      <td style={{ fontSize: 12.5 }}>
                        {item.application === null ? (
                          <span className="tx-subtle">{STR.access.catalog.none}</span>
                        ) : (
                          item.application.name
                        )}
                      </td>
                      <td>{sw(item, 'requestable', STR.access.catalog.toggleRequestable(item.name))}</td>
                      <td>{sw(item, 'hrCanGrant', STR.access.catalog.toggleHrCanGrant(item.name))}</td>
                      <td className="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {count === null ? STR.common.dash : count}
                      </td>
                    </tr>
                  );
                }),
              ])}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
